import { apiClient } from './apiClient';

const BASE = '/api/app/missing-books-extn';

export interface MissingBookFilter {
  page: number;
  pageSize: number;
  q?: string;
  booksellerId?: string;
  onlyUnresolved?: boolean;
  sort?: 'requestCount' | 'lastSeenAt' | 'firstSeenAt' | 'title';
  dir?: 'asc' | 'desc';
}

export const DEFAULT_MISSING_BOOK_FILTER: MissingBookFilter = {
  page: 1,
  pageSize: 25,
  onlyUnresolved: true,
  sort: 'requestCount',
  dir: 'desc',
};

export interface MissingBookDto {
  id: string;
  booksellerBookId: string;
  isbn?: string | null;
  title?: string | null;
  author?: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  requestCount: number;
  resolvedAt?: string | null;
  notes?: string | null;
  booksellerId: string;
  concurrencyStamp: string;
}

export interface BooksellerRefDto {
  id: string;
  name: string;
  slug: string;
  displayName: string;
}

export interface MissingBookWithNavigationPropertiesDto {
  missingBook: MissingBookDto;
  bookseller: BooksellerRefDto;
}

export interface MissingBooksPageResult {
  totalCount: number;
  items: MissingBookWithNavigationPropertiesDto[];
}

// Maps the ABP paged DTO's nested property names (which the backend serialises
// starting with "missingBook." because that's the navigation root) into the
// sort tokens the API expects. Keeping this here rather than in the table so
// every caller uses the same canonical mapping.
function buildSorting(sort?: string, dir?: string): string | undefined {
  const d = dir === 'asc' ? 'asc' : 'desc';
  switch (sort) {
    case 'requestCount': return `missingBook.RequestCount ${d}`;
    case 'lastSeenAt':   return `missingBook.LastSeenAt ${d}`;
    case 'firstSeenAt':  return `missingBook.FirstSeenAt ${d}`;
    case 'title':        return `missingBook.Title ${d}`;
    default: return undefined;
  }
}

function toQuery(f: MissingBookFilter): Record<string, string | number | boolean> {
  const q: Record<string, string | number | boolean> = {
    SkipCount: (f.page - 1) * f.pageSize,
    MaxResultCount: f.pageSize,
  };
  if (f.q) q.FilterText = f.q;
  if (f.booksellerId) q.BooksellerId = f.booksellerId;
  // onlyUnresolved → ResolvedAtMax=null is not expressible; instead ask server
  // for rows with ResolvedAtMin+Max both unset. The list endpoint treats "both
  // null" as "any" — so we can't filter there. We'll filter client-side in the
  // table for simplicity, and rely on sort to put active gaps at the top.
  const sorting = buildSorting(f.sort, f.dir);
  if (sorting) q.Sorting = sorting;
  return q;
}

export const missingBooksService = {
  list: async (filters: MissingBookFilter): Promise<MissingBooksPageResult> => {
    const { data } = await apiClient.get<MissingBooksPageResult>(`${BASE}/list`, {
      params: toQuery(filters),
    });
    return data;
  },

  exportCsv: async (filters: MissingBookFilter): Promise<Blob> => {
    // Whole filtered set, not a page — SkipCount/MaxResultCount are ignored server-side for export.
    const q = toQuery(filters);
    delete q.SkipCount;
    delete q.MaxResultCount;
    const response = await apiClient.get(`${BASE}/export-csv`, {
      params: q,
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  resolve: async (id: string): Promise<MissingBookDto> => {
    const { data } = await apiClient.post<MissingBookDto>(`${BASE}/${id}/resolve`);
    return data;
  },
};
